import { getApiErrorMessage } from "utils/apiErrorMessage";

/**
 * Error code mappings for TenantReportException from the backend.
 * These codes are thrown by apps/tenants/api/exceptions.py
 */
export const TENANT_REPORT_ERROR_CODES: Record<string, string> = {
  "6000":
    "El reporte debe estar en estado 'En Progreso' o 'Completado' para vista previa",
  "6001": "El reporte no está activo para vista previa",
  "6002": "El reporte ya se encuentra en estado 'Completado'",
  "6003": "El reporte no está activo para ser completado",
  "6004": "El reporte debe estar en estado 'En Progreso' para ser completado",
  "6005": "Error al actualizar la configuración del reporte.",
  "6006": "Error al establecer el precio del reporte.",
  "6007": "Error al establecer el precio del fiador.",
  "6008":
    "Error al actualizar el estado del reporte. Por favor contacta a soporte.",
  "6009":
    "Error al enviar notificación. El reporte fue creado pero la notificación falló.",
  "6010":
    "No hay suficientes reportes disponibles. Verifica que la organización tenga cuotas disponibles.",
  "6011": "Error al crear la carpeta de Multiexpediente.",
  "6012": "Error al crear la investigación.",
  "6013": "Error al crear el link de pago.",
  "6999": "Ha ocurrido un error inesperado.",
} as const;

export type TenantReportErrorCode = keyof typeof TENANT_REPORT_ERROR_CODES;

const DEFAULT_ERROR_MESSAGE = "Ha ocurrido un error inesperado.";

/**
 * Gets the localized error message for a TenantReport API error.
 * Handles the error response format: { code: "6005", message: "..." }
 *
 * @param error - The error object from axios catch block
 * @param fallbackMessage - Custom fallback message for unknown errors (optional)
 * @returns Localized Spanish error message
 */
export const getTenantReportErrorMessage = (
  error: unknown,
  fallbackMessage?: string,
): string => {
  const err = error as {
    response?: {
      data?: {
        code?: string;
        message?: unknown;
        detail?: unknown;
      };
    };
  };

  const code = err?.response?.data?.code;

  // If we have a mapped code, use our Spanish translation
  if (code && code in TENANT_REPORT_ERROR_CODES) {
    return TENANT_REPORT_ERROR_CODES[code];
  }

  // Handle legacy error format (detail field)
  const rawMessage = err?.response?.data?.message;
  const rawDetail = err?.response?.data?.detail;
  if (
    rawMessage ===
      "The organization does not have identity validation module." ||
    rawDetail === "The organization does not have identity validation module."
  ) {
    return "La organización no tiene el módulo de validación biométrica.";
  }

  // `message`/`detail` estaban tipados `string` pero pueden traer el
  // diccionario de errores del serializer: `getApiErrorMessage` garantiza que
  // lo que sale de aqui es texto mostrable y nunca un objeto.
  return getApiErrorMessage(error, fallbackMessage || DEFAULT_ERROR_MESSAGE);
};
